import { Autocomplete, InputLabel, MenuItem, styled as muiStyled, TextField, Typography } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-media';
import styled from 'styled-components';

import { COLOR, GLOBAL_MEDIA_QUERIES, MEDIA_QUERIES_BREAKPOINTS } from '@/globalStyles';
import { useAllUsers } from '@/hooks/useAllUsers/useAllUsers';
import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';
import { UserOption } from '@/pages/NewRequest/useNewRequestLogic';

interface UsersSelectProps {
    name: string;
    value?: UserOption;
    onChange: (name, value) => void;
    disabled: boolean;
    className?: string;
}

const UsersSelect = ({ name, value, onChange, disabled, className }: UsersSelectProps) => {
    const { t } = useTranslation();
    const matchSmallTablet = useMedia({ query: GLOBAL_MEDIA_QUERIES.stablet });
    const allUsers = useAllUsers();
    const handleChange = useCallback(
        (_, newValue) => {
            if (!newValue) return;
            mixpanelTracking(MIXPANEL_ACTIONS.SELECT_EMPLOYEE_NEW_REQUEST_ON_BEHALF);
            onChange(name, newValue);
        },
        [onChange, name]
    );

    const options = useMemo(
        () =>
            allUsers?.map(user => ({
                label: `${user?.firstName} ${user?.lastName}`,
                userMail: user?.username,
                userId: user?.credentialId,
            })),
        [allUsers]
    );

    return (
        <div className={className}>
            {matchSmallTablet && (
                <InputLabel>
                    <Typography color={COLOR.darkGray} variant="table_text">
                        {t('newAbsenceView_reportingFor_title')}
                    </Typography>
                </InputLabel>
            )}
            {!!options && (
                <Autocomplete
                    value={value}
                    isOptionEqualToValue={(option, value) => option?.userId === value?.userId}
                    onChange={handleChange}
                    options={options}
                    getOptionLabel={option => option?.label}
                    renderOption={(props, option) => {
                        return (
                            <StyledOption key={option?.userId} {...props}>
                                <Typography variant="profile_title">{option?.label}</Typography>
                                <Typography variant="profile_sub_title">{option?.userMail}</Typography>
                            </StyledOption>
                        );
                    }}
                    renderInput={params => <TextField {...params} />}
                    disabled={disabled}
                    disableClearable={true}
                />
            )}
        </div>
    );
};

const StyledOption = muiStyled(MenuItem)`
    && {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
`;

const Memoized = memo(UsersSelect);
const Styled = styled(Memoized)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    & > div {
        width: 60%;
        @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
            width: 100%;
        }
    }
    .MuiAutocomplete-root .MuiOutlinedInput-root {
        height: 2.5rem;
        padding: 0 0.5rem;
    }
    .Mui-disabled {
        cursor: not-allowed;
    }
`;

export default Styled;
